// Sign-in state (REQ-SEC-01): passkey first, password as fallback; the session ends with the
// app session or after 12 hours. Step-up (REQ-SEC-05) asks for Face ID or fingerprint again.
// An invitation or password-reset link signs the person in once and asks them to set up how they
// sign in from then on: Face ID or fingerprint on this device, then a password (BD-73).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { supabase, dataClient } from "../lib/supabase";
import { callFunction, ApiError } from "../lib/api";
import { saveOfflineUser, offlineUser, unlockOffline } from "../offline/unlock";
import { carriesSessionTokens, clearEmailLink, needsSetUp, readEmailLink, type EmailLink } from "./emailLink";

export type Me = {
  user_account_id: string; auth_user_id: string; person_id: string; full_name: string; email: string | null;
  team: string; job_title: string | null; roles: string[]; has_passkey: boolean; is_protected: boolean; step_up_fresh: boolean;
};

type AuthState = {
  ready: boolean;
  session: Session | null;
  me: Me | null;
  /** Unlocked without signal with the phone's Face ID or fingerprint; changes wait for sync. */
  offline: boolean;
  canUnlockOffline: boolean;
  unlockOffline(): Promise<boolean>;
  signInWithPasskey(): Promise<void>;
  signInWithPassword(email: string, password: string): Promise<void>;
  registerPasskey(label?: string): Promise<void>;
  stepUp(): Promise<void>;
  /** An invitation or reset link that no longer works ("otp_expired" when expired or already used). */
  linkProblem: string | null;
  clearLinkProblem(): void;
  /** Signed in from an invitation or reset link: Face ID and a password still to be set up. */
  setUpPending: boolean;
  setPassword(password: string): Promise<void>;
  finishSetUp(): void;
  signOut(): Promise<void>;
  refreshMe(): Promise<void>;
  isManager: boolean;
};

const Ctx = createContext<AuthState | null>(null);
const MAX_SESSION_MS = 12 * 60 * 60 * 1000;
const SIGNED_IN_AT = "rk-signed-in-at";
const SET_UP = "rk-set-up";

let linkResult: Promise<string | null> | null = null;
/** The link this page was opened with, if any, is used once per page load. */
function emailLinkOnce(): Promise<string | null> {
  if (!linkResult) {
    const link = readEmailLink(window.location.href);
    // SR1-24: session tokens in the address bar sign no one in; they are only taken out of it, and out of history.
    if (link || carriesSessionTokens(window.location.href)) clearEmailLink();
    linkResult = link ? openEmailLink(link) : Promise.resolve(null);
  }
  return linkResult;
}

/** Signs in with an invitation or reset link, checked once by the sign-in service. Returns the problem code when the link does not work. */
async function openEmailLink(link: EmailLink): Promise<string | null> {
  if (link.kind === "problem") return link.code;
  const { error } = await supabase.auth.verifyOtp({ token_hash: link.tokenHash, type: link.type });
  if (error) return (error as { code?: string }).code ?? "otp_expired";
  sessionStorage.setItem(SIGNED_IN_AT, String(Date.now()));
  if (needsSetUp(link.type)) sessionStorage.setItem(SET_UP, "1");
  return null;
}

/** How long start-up waits for the sign-in service's answer about the session. */
const SESSION_CHECK_MS = 4000;

/** What the sign-in service answers when the session it was asked about no longer holds (SR1-16). */
const SESSION_OVER = /session_expired|session_not_found|refresh_token_not_found|refresh_token_already_used|Invalid Refresh Token/i;

/**
 * Whether the sign-in service still holds this session (SR1-16). Renewing the session asks the service itself, which keeps
 * the session's start and its 12-hour time-box (sessions_timebox, set by the deploy) and knows a signed-out session; no
 * function is added for it. Only those answers end the session: no signal, or a refusal for another reason, keeps it,
 * as before, and the database still refuses an ended session's reads and changes.
 */
async function sessionHeld(): Promise<boolean> {
  // Without signal the app starts at once, as before; a slow answer is not waited for beyond a few seconds.
  if (navigator.onLine === false) return true;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const slow = new Promise<boolean>((resolve) => { timer = setTimeout(() => resolve(true), SESSION_CHECK_MS); });
  const asked = Promise.resolve()
    .then(() => supabase.auth.refreshSession())
    .then(({ error }) => {
      const said = Object(error) as { code?: unknown; message?: unknown };
      return !SESSION_OVER.test(`${String(said.code)} ${String(said.message)}`);
    }, () => true);
  const held = await Promise.race([asked, slow]);
  clearTimeout(timer);
  return held;
}

async function loadMe(): Promise<Me | null> {
  // Opened without signal: start straight away as the user last seen on this phone, without waiting for the network.
  if (typeof navigator !== "undefined" && navigator.onLine === false && offlineUser()) return offlineUser()!.me;
  const { data, error } = await dataClient.from("v_me").select("*").maybeSingle();
  if (error || !data) {
    // Without signal, keep working as the user last seen on this phone.
    const cached = offlineUser();
    return error && cached ? cached.me : null;
  }
  const me = data as Me;
  const { data: creds } = await dataClient.from("passkey_credential").select("credential_id").eq("user_account_id", me.user_account_id).is("removed_at", null);
  saveOfflineUser(me, (creds ?? []).map((c: { credential_id: string }) => c.credential_id));
  return me;
}

export function passkeySupported(): boolean {
  return typeof window !== "undefined" && "PublicKeyCredential" in window;
}

/** Signs in with Face ID or fingerprint: the passkey function checks it and answers a one-time link, checked here once. */
async function passkeySignIn(): Promise<void> {
  const opts = await callFunction<{ challenge_id: string; options: any }>("passkey/login-options", {}, { auth: false });
  const response = await startAuthentication({ optionsJSON: opts.options });
  const r = await callFunction<{ ok: boolean; token_hash?: string; message?: string; code?: string }>("passkey/login-verify", { challenge_id: opts.challenge_id, response }, { auth: false });
  if (!r.ok || !r.token_hash) throw new ApiError(r.message ?? "Sign-in failed. Try again.", r.code ?? "invalid", 401);
  const { error } = await supabase.auth.verifyOtp({ token_hash: r.token_hash, type: "email" });
  if (error) throw new ApiError("Sign-in failed. Try again.", "invalid", 401);
}

/** Face ID or fingerprint asked for again (REQ-SEC-05). */
async function confirmWithFaceId(): Promise<void> {
  const opts = await callFunction<{ challenge_id: string; options: any }>("passkey/step-up-options", {});
  const response = await startAuthentication({ optionsJSON: opts.options });
  const r = await callFunction<{ ok: boolean; message?: string; code?: string }>("passkey/step-up-verify", { challenge_id: opts.challenge_id, response });
  if (!r.ok) throw new ApiError(r.message ?? "Face ID or fingerprint could not be confirmed.", r.code ?? "step_up_required", 401);
}

/** Saves the person's new password. */
async function savePassword(password: string): Promise<void> {
  // The time is kept on the login: the deploy re-sends an invitation only to a login never set up (BD-73).
  const { error } = await supabase.auth.updateUser({ password, data: { sign_in_set_up_at: new Date().toISOString() } });
  if (!error) return;
  const code = (error as { code?: string }).code;
  if (code === "same_password") throw new ApiError("That is the password you have now. Choose a new one.", "invalid", 422);
  if (code === "weak_password") throw new ApiError("Choose a longer password, with letters and numbers.", "invalid", 422);
  throw new ApiError("The password could not be saved. Try again.", code ?? "invalid", 422);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  // Read by registerPasskey without making it a new function each time the person is loaded again.
  const hasPasskey = useRef(false);
  hasPasskey.current = !!me?.has_passkey;
  const [offline, setOffline] = useState(false);
  const [linkProblem, setLinkProblem] = useState<string | null>(null);
  const [setUpPending, setSetUpPending] = useState(() => sessionStorage.getItem(SET_UP) === "1");

  const refreshMe = useCallback(async () => setMe(await loadMe()), []);

  useEffect(() => {
    let alive = true;
    emailLinkOnce().then((problem) => {
      if (!alive) return;
      setLinkProblem(problem);
      setSetUpPending(sessionStorage.getItem(SET_UP) === "1");
      return supabase.auth.getSession();
    }).then(async (got) => {
      if (!got) return;
      const { data } = got;
      const at = Number(sessionStorage.getItem(SIGNED_IN_AT) ?? 0);
      // SR1-16 (Decision 150 #2c): the sign-in service is asked too, before any screen reads data, so a session it has
      // ended (past its 12-hour time-box, or signed out elsewhere) goes straight to sign-in.
      if (data.session && (!at || Date.now() - at > MAX_SESSION_MS || !(await sessionHeld()))) {
        await supabase.auth.signOut();
        if (alive) { setSession(null); setReady(true); }
        return;
      }
      if (!alive) return;
      setSession(data.session);
      if (data.session) setMe(await loadMe());
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (!s) setMe(null);
    });
    const expired = () => { void supabase.auth.signOut(); };
    window.addEventListener("rk:session-expired", expired);
    const timer = window.setInterval(() => {
      const at = Number(sessionStorage.getItem(SIGNED_IN_AT) ?? 0);
      if (at && Date.now() - at > MAX_SESSION_MS) void supabase.auth.signOut();
    }, 60_000);
    return () => { alive = false; sub.subscription.unsubscribe(); window.removeEventListener("rk:session-expired", expired); window.clearInterval(timer); };
  }, []);

  const afterSignIn = useCallback(async () => {
    sessionStorage.setItem(SIGNED_IN_AT, String(Date.now()));
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
    setMe(await loadMe());
  }, []);

  const signInWithPasskey = useCallback(async () => {
    await passkeySignIn();
    await afterSignIn();
  }, [afterSignIn]);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) throw new ApiError("That email and password do not match. Try again, or ask the CEO to reset your password.", "invalid", 401);
    await afterSignIn();
  }, [afterSignIn]);

  const stepUp = useCallback(confirmWithFaceId, []);

  const registerPasskey = useCallback(async (label?: string) => {
    // SR1-03 (Decision 150 #2c): a further device is added only with Face ID from one already registered.
    if (hasPasskey.current) await stepUp();
    const opts = await callFunction<{ challenge_id: string; options: any }>("passkey/register-options", {});
    const response = await startRegistration({ optionsJSON: opts.options });
    const r = await callFunction<{ ok: boolean; message?: string; code?: string }>("passkey/register-verify", { challenge_id: opts.challenge_id, response, device_label: label ?? navigator.userAgent.slice(0, 60) });
    if (!r.ok) throw new ApiError(r.message ?? "Face ID or fingerprint could not be registered.", r.code ?? "invalid", 422);
    await refreshMe();
  }, [stepUp, refreshMe]);

  const setPassword = useCallback(savePassword, []);

  const finishSetUp = useCallback(() => {
    sessionStorage.removeItem(SET_UP);
    setSetUpPending(false);
  }, []);

  const signOut = useCallback(async () => {
    sessionStorage.removeItem(SIGNED_IN_AT);
    sessionStorage.removeItem(SET_UP);
    setSetUpPending(false);
    setOffline(false);
    await supabase.auth.signOut();
  }, []);

  const doUnlockOffline = useCallback(async () => {
    const u = offlineUser();
    if (!u) return false;
    const ok = await unlockOffline(u).catch(() => false);
    if (ok) { setMe(u.me); setOffline(true); }
    return ok;
  }, []);

  // Once signed in again online, offline mode ends.
  useEffect(() => { if (session) setOffline(false); }, [session]);

  const value = useMemo<AuthState>(() => ({
    ready, session, me, offline, canUnlockOffline: !!offlineUser()?.credentialIds.length, unlockOffline: doUnlockOffline,
    signInWithPasskey, signInWithPassword, registerPasskey, stepUp, signOut, refreshMe,
    linkProblem, clearLinkProblem: () => setLinkProblem(null), setUpPending, setPassword, finishSetUp,
    isManager: !!me?.roles.some((r) => r === "ceo" || r === "gm")
  }), [ready, session, me, offline, doUnlockOffline, signInWithPasskey, signInWithPassword, registerPasskey, stepUp, signOut, refreshMe,
    linkProblem, setUpPending, setPassword, finishSetUp]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
